<template>
    <div class="">
        <div 
            v-for="note in noteList"
            :key="note._localId"
            class="mb-2 bg-gray-100"
            :class="note._mode === 'delete' ? 'bg-danger-100' : 'bg-gray-100'"
            style="border-left: 2px solid var(--gray-300);"
        >
            <div 
                v-if="note._mode === 'edit'" 
                :key="`${note._localId}-edit`" 
                class="w-100"
            >
                <b-form-textarea 
                    ref="editNoteTextArea"
                    v-model="note.content"
                    placeholder="Enter your note..."
                    @keydown.enter.exact.prevent="saveEditedNote(note)"
                ></b-form-textarea>
                <div key="activeNoteButtons" class="d-flex gap-2 my-2 align-items-start">
                    <b-button 
                        size="sm" 
                        variant="primary" 
                        :disabled="!note.content.trim().length || saving"
                        @click="saveEditedNote(note)"
                    >Save</b-button>
                    <b-button 
                        size="sm" 
                        variant="gray-primary" 
                        :disabled="saving" 
                        @click="cancelEdit(note)"
                    >Cancel</b-button>
                    <span class="special-text font-size-70 ml-auto text-muted">&crarr; Hit Enter to Save</span>
                </div>
            </div>
            <div v-else :key="`${note._localId}-not-edit`" class="w-100 font-size-80 px-2 py-1 d-flex gap-3 align-items-center justify-content-between">
                <div class="text-show-breaks flex-shrink-1">{{ note.content }}</div>
                <div class="d-flex gap-3 align-items-center text-right flex-shrink-0">
                    <div class="d-flex align-items-center">
                        <b-button v-if="note.can_edit" variant="minimal-secondary" size="sm" class="" @click="startEdit(note)">
                            <b-icon icon="pencil" aria-hidden="true"></b-icon>
                        </b-button>
                        <b-button v-if="note.can_delete" variant="minimal-danger" size="sm" class="" @click="startDelete(note)">
                            <b-icon icon="trash" aria-hidden="true"></b-icon>
                        </b-button>
                    </div>
                    <div class="text-right">
                        <div class="font-size-70 text-gray-500">{{ note.created_by.full_name }}</div>
                        <div class="font-size-60 text-gray-400">{{ formatDate(note.created) }}</div>
                    </div>
                </div>
            </div>
        </div>
        <!-- TODO - add internal/external visibility selector -->
        <b-form-textarea 
            v-if="newNote" 
            ref="newNoteTextArea"
            v-model="newNote.content"
            placeholder="Enter your note..."
            @keydown.enter.exact.prevent="canSaveNewNote ? saveNewNote() : null"
        ></b-form-textarea>
        <div v-if="newNote" key="activeNoteButtons" class="d-flex gap-2 mt-2 align-items-start">
            <b-button 
                size="sm" 
                variant="primary" 
                :disabled="!canSaveNewNote" 
                @click="saveNewNote"
            >Save</b-button>
            <b-button 
                size="sm" 
                variant="gray-primary" 
                :disabled="saving" 
                @click="clearNewNote"
            >Cancel</b-button>
            <span class="special-text font-size-70 ml-auto text-muted">&crarr; Hit Enter to Save</span>
        </div>
        <div v-else-if="showNewNoteButton" class="d-flex gap-2 mt-2 align-items-start">
            <b-button 
                v-if="showNewNoteButton" 
                size="sm" 
                variant="minimal-primary" 
                :disabled="loadingNotes || saving" 
                @click="startNewNote"
            >+ Add Note</b-button>
        </div>
    </div>
</template>

<script>
import { nanoid } from 'nanoid';
import { currentUserInfoMixin, errorHandling } from '@/js/custom-vue-mixins';
import { suppliosApi } from '@/js/api-service';
import { orderBy } from 'lodash';
import { DateTime } from 'luxon';

export default {
    name: 'TenantUserNotes',
    components: {},
    mixins: [
        currentUserInfoMixin,
        errorHandling,
    ],
    props: {
        tuId: {
            type: String,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        locked: {  // set true from parent to lock editing (because something in parent makes it a read-only context)
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loadingNotes: false,
            saving: false,
            deleting: false,
            // should we let parent pass these in?  if so, make a copy, pass it back up on changes
            noteStore: {},  // map of note.id to note obj for easy lookup
            newNote: null,
        }
    },
    computed: {
        showNewNoteButton() {
            return !this.readOnly && !this.newNote;
        },
        canSaveNewNote() {
            return !this.saving && !this.deleting && !this.loadingNotes && this.newNote && this.newNote.content.trim().length > 0;
            // add something about making sure visibility is set?
        },
        noteList() {
            return orderBy(Object.values(this.noteStore), ['created'], ['asc']);  // with the "Add Note" button at the bottom, it makes sense to show most recent last
        },
    },
    created() {
        
    },
    mounted() {
        this.fetchNotes();
    },
    methods: {
        async fetchNotes() {
            this.loadingNotes = true;
            let resp;
            try {
                resp = await suppliosApi.tuNotes.list_for_tu(this.tuId);
                this.setLocalNotes(resp.data);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.loadingNotes = false;
        },
        makeLocalNote(apiNote) {
            return {
                ...apiNote,
                _mode: 'view', 
                _localId: nanoid(),
                _originalContent: apiNote.content,
            }
        },
        setLocalNotes(notes) {
            this.noteStore = Object.fromEntries(
                notes.map(n => [n.id, this.makeLocalNote(n)])
            );
        },
        startNewNote() {
            // TODO - select internal/external based on where you are using this, who the TU is, etc.
            const newNote = {
                content: '',
                visibility: 'internal',  // TODO - make this dynamic, set based on where its being used, current TU, etc.
            };
            this.newNote = newNote;
            this.$nextTick(() => {
                this.$refs.newNoteTextArea.focus();
            });
        },
        clearNewNote() {
            this.newNote = null;
        },
        setNoteMode(note, mode) {
            this.$set(note, '_mode', mode);
        },
        formatDate(isoString) {
            return isoString ? DateTime.fromISO(isoString).toLocaleString(DateTime.DATE_MED) : '';
        },
        async saveNewNote() {
            if (!this.newNote) return;
            this.saving = true;
            let resp;
            let returnedNote = null;
            try {
                resp = await suppliosApi.tuNotes.create_for_tu(this.tuId, {
                    content: this.newNote.content,
                    visibility: this.newNote.visibility,
                });
                returnedNote = resp.data;
            } catch (error) {
                this.apiErrorHandler(error);
                return;
            }
            this.$set(this.noteStore, returnedNote.id, this.makeLocalNote(returnedNote));
            this.saving = false;
            this.clearNewNote();
            // TODO - EMIT TO PARENT -- the new list of notes
        },
        async startDelete(note) {
            this.deleting = true;
            this.setNoteMode(note, 'delete');
            const confirmOk = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to delete this note?  This cannot be undone.`, 
                {
                    title: "Please Confirm",
                    centered: true,
                    size: 'sm',
                    okTitle: 'Yes, Delete',
                    okVariant: 'danger',
                    cancelTitle: 'No, Cancel',
                    cancelVariant: 'gray-dark',
                    footerClass: 'border-0',
                });
            let deleteSuccess = false;
            if (confirmOk) {
                // DO THE DELETE
                try {
                    await suppliosApi.tuNotes.delete_for_tu(this.tuId, note.id);
                    // remove from local list
                    this.$delete(this.noteStore, note.id);
                    deleteSuccess = true;
                } catch (error) {
                    this.apiErrorHandler(error);
                }
            } else {
                // if user cancelled, just set back to view mode, no emit, nothing changed
                this.setNoteMode(note, 'view');
            }
            // at the end of everything, if we did delete, emit to parent
            this.deleting = false;
            if (deleteSuccess) {
                this.emitToParent();
            }
        },
        async startEdit(note) {
            // Store original content in case user cancels
            note._originalContent = note.content;
            this.setNoteMode(note, 'edit');
            this.$nextTick(() => {
                // these refs are arrays because they are inside v-for
                // but only one should be in edit mode at a time, so we find if we have any or 1 and focus it
                const editAreas = this.$refs.editNoteTextArea;
                if (Array.isArray(editAreas) && editAreas[0]) {
                    editAreas[0].focus();
                }
            });
        },
        async saveEditedNote(note) {
            if (!note.content || note.content.trim().length === 0) return;
            this.saving = true;
            let resp;
            try {
                resp = await suppliosApi.tuNotes.update_for_tu(this.tuId, {
                    id: note.id,
                    content: note.content,
                    visibility: note.visibility,
                });
                // Update the note with response data
                const updatedNote = this.makeLocalNote(resp.data);
                this.$set(this.noteStore, note.id, updatedNote);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.saving = false;
            this.emitToParent();
        },
        cancelEdit(note) {
            // Restore original content
            note.content = note._originalContent;
            this.setNoteMode(note, 'view');
        },
        emitToParent() {
            this.$emit('updated', [...this.noteList]);
        },
    },
}
</script>

<style lang="scss" scoped>
</style>